import type {
  CanonicalParty,
  CanonicalQuote,
  CanonicalVehicle,
  CanonicalVehicleType,
  QuoteResponse,
  ResolvedOwner,
} from "../../../types/lifecycle.ts";
export { fingerprintQuote } from "../../../services/Integrations/LifecycleService.ts";
export type Party = Required<
  Pick<
    CanonicalParty,
    | "firstName"
    | "surname"
    | "familyName"
    | "companyName"
    | "pin"
    | "settlementCode"
    | "street"
    | "customerType"
    | "phone"
  >
>;
export type PartyKey = "owner" | "holder" | "driver";
export type PartyField = keyof Party;
export type VehicleField = Exclude<
  keyof CanonicalVehicle,
  "providerMappings" | "vehicleType"
>;
export type DirtyField =
  | `${PartyKey}.${PartyField}`
  | `vehicle.${VehicleField}`;
export type DirtyFields = Partial<Record<DirtyField, true>>;
export interface Draft {
  vehicle: CanonicalVehicle;
  owner: Party;
  holder: Party;
  driver: Party;
}
export interface SearchInputs {
  plate: string;
  certNumber: string;
  pin: string;
  purpose: string;
  vehicleType: CanonicalVehicleType | "";
  installments: number;
  coverageStart: string;
}
export const PARTY_KEYS: PartyKey[] = ["holder", "owner", "driver"];
export const PARTY_LABELS: Record<PartyKey, string> = {
  owner: "Собственик",
  holder: "Застраховащ",
  driver: "Обичаен водач",
};
export const VEHICLE_TYPE_OPTIONS: Record<CanonicalVehicleType, string> = {
  passenger: "Лек автомобил",
  freight: "Товарен автомобил",
  bus: "Автобус",
  motorcycle: "Мотоциклет / АТВ",
  moped: "Мотопед",
  trailer: "Ремарке / полуремарке",
  tractor: "Трактор / земеделска техника",
};
export const isDefaultProviderEnabled = (provider: string) =>
  !["dallbogg", "armeec", "euroins"].includes(provider);
export const seedParty = (party: CanonicalParty = {}): Party => ({
  firstName: party.firstName ?? "",
  surname: party.surname ?? "",
  familyName: party.familyName ?? "",
  companyName:
    party.customerType === "legal"
      ? party.companyName ?? party.name ?? party.firstName ?? ""
      : "",
  pin: party.pin ?? "",
  settlementCode: party.settlementCode ?? "",
  street: party.street ?? "",
  customerType: party.customerType ?? "natural",
  phone: party.phone ?? "",
});
export const roleToParty = (role: ResolvedOwner): Party => {
  const names = (role.name ?? "").trim().split(/\s+/);
  return seedParty({
    name: role.name,
    firstName: names[0] ?? "",
    surname: names.length > 2 ? names.slice(1, -1).join(" ") : "",
    familyName: names.length > 1 ? names[names.length - 1] : "",
    pin: role.id ?? "",
    settlementCode: role.addresses?.[0]?.ekatte ?? "",
    street: role.addresses?.[0]?.line ?? "",
    customerType: role.type === "legal" ? "legal" : "natural",
  });
};

const streetForComparison = (value: string) => {
  const text = value
    .normalize("NFKC")
    .toLocaleLowerCase("bg")
    .replace(/(?:№|\bno\.?)\s*(?=\d)/gu, "")
    .replace(/\s+/gu, " ")
    .trim();
  const prefix = text.match(
    /^(бул\.?\s*\/\s*ул\.?|ул\.?\s*\/\s*бул\.?|ул\.?|улица|бул\.?|булевард)\s+/u
  );
  return {
    line: text.slice(prefix?.[0].length ?? 0),
    // "бул./ул." is a generic label, not a change from street to boulevard.
    kind:
      !prefix || prefix[1].includes("/")
        ? null
        : prefix[1].startsWith("ул")
        ? "street"
        : "boulevard",
  };
};
export const sameStreetAddress = (left: string, right: string) => {
  const a = streetForComparison(left);
  const b = streetForComparison(right);
  return a.line === b.line && (!a.kind || !b.kind || a.kind === b.kind);
};

const pickDirty = <T extends object>(
  value: T,
  dirty: DirtyFields,
  group: "vehicle" | PartyKey
): Partial<T> =>
  Object.fromEntries(
    Object.entries(value).filter(([key]) => dirty[`${group}.${key}`])
  ) as Partial<T>;
// Booked locally only (localWrite.client.phone, localWrite.vehicle.color). No insurer
// mapping exists yet, so they must neither reach the quote nor make a priced quote
// stale when edited.
const LOCAL_ONLY_PARTY_FIELDS: PartyField[] = ["phone"];
const pickQuoted = (party: Party, dirty: DirtyFields, group: PartyKey) => {
  const picked = pickDirty(party, dirty, group);
  for (const key of LOCAL_ONLY_PARTY_FIELDS) delete picked[key];
  return picked;
};
const LOCAL_ONLY_VEHICLE_FIELDS: VehicleField[] = ["color"];
const pickQuotedVehicle = (vehicle: CanonicalVehicle, dirty: DirtyFields) => {
  const picked = pickDirty(vehicle, dirty, "vehicle");
  for (const key of LOCAL_ONLY_VEHICLE_FIELDS) delete picked[key];
  return picked;
};
export const buildQuote = (
  inputs: SearchInputs,
  draft: Draft | null,
  dirty: DirtyFields
): CanonicalQuote => {
  const vehicle = draft ? pickQuotedVehicle(draft.vehicle, dirty) : {};
  if (inputs.vehicleType) vehicle.vehicleType = inputs.vehicleType;
  const owner = draft ? pickQuoted(draft.owner, dirty, "owner") : {};
  const holder = draft ? pickQuoted(draft.holder, dirty, "holder") : {};
  const driver = draft ? pickQuoted(draft.driver, dirty, "driver") : {};
  return {
    registrationPlate: inputs.plate.trim().toUpperCase(),
    certificateNumber: inputs.certNumber.trim(),
    ownerIdentifier: String(holder.pin ?? inputs.pin).trim(),
    installments: inputs.installments,
    ...(inputs.coverageStart ? { coverageStart: inputs.coverageStart } : {}),
    ...(inputs.purpose ? { purpose: inputs.purpose } : {}),
    ...(Object.keys(vehicle).length ? { vehicle } : {}),
    ...(Object.keys(owner).length ? { owner } : {}),
    ...(Object.keys(holder).length ? { holder } : {}),
    ...(Object.keys(driver).length ? { driver } : {}),
  };
};
export const resolveDraft = (
  response: QuoteResponse,
  carried: Draft | null,
  dirty: DirtyFields
): Draft => {
  const holder = response.holder ?? {};
  const owner = response.owners?.[0]
    ? roleToParty(response.owners[0])
    : seedParty(holder);
  const draft: Draft = {
    vehicle: response.vehicle ?? {},
    owner,
    holder: holder.roles?.acquirer
      ? roleToParty(holder.roles.acquirer)
      : response.resolved?.leasing?.lesseeRequired
      ? seedParty()
      : seedParty(holder),
    driver: holder.roles?.user
      ? { ...roleToParty(holder.roles.user), customerType: "natural" }
      : owner.customerType === "legal"
      ? seedParty()
      : { ...owner },
  };
  if (!carried) return draft;
  return {
    vehicle: {
      ...draft.vehicle,
      ...pickDirty(carried.vehicle, dirty, "vehicle"),
    },
    owner: { ...draft.owner, ...pickDirty(carried.owner, dirty, "owner") },
    holder: { ...draft.holder, ...pickDirty(carried.holder, dirty, "holder") },
    driver: { ...draft.driver, ...pickDirty(carried.driver, dirty, "driver") },
  };
};
export const inferCustomerType = (identifier: string) => {
  const digits = identifier.replace(/\D/g, "");
  return digits.length === 10
    ? "natural"
    : [9, 13].includes(digits.length)
    ? "legal"
    : null;
};
